import { supplierCostInClientCurrency } from './brosRecordMoney';

// Row shapes for the BR OS Bookings / Invoices lists (BROSRecordsList).
//
// Fan, 2026-10-28: "Bookings and Invoices, please group by due date. And the
// bookings by closing date should be under the Sales tab."
//
// Every row carries the same normalised fields so the list can group and
// total any basis the same way:
//   _mk        month bucket (YYYY-MM)
//   _cur       currency of the _ values
//   _excluded  listed but kept out of the totals
// Bookings: _client, _supplier, _margin, _nights. Invoices: _amount,
// _paid, _outstanding.

export function monthOf(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  return s.length >= 7 ? s.slice(0, 7) : null;
}

// Same key as backend services/bros_due_date_attribution.invoice_key.
export function invoiceKey(inv) {
  return String(inv?.bros_id || inv?.invoice_number || inv?.id || '');
}

// Same rule as backend ex_vat_amount: subtotal, else total - tax.
export function invoiceExVat(inv) {
  if (inv?.subtotal != null && inv.subtotal !== '') return Number(inv.subtotal) || 0;
  return (Number(inv?.total_amount) || 0) - (Number(inv?.tax_amount) || 0);
}

// Due-date basis (P&L > Bookings): one row per booking per month, valued at
// the booking's share of that month's revenue, exactly the rows behind the
// P&L chart (GET /reports/bros/dashboard?include_bookings=true), in GBP.
export function bookingsByDueMonth(months, bookings) {
  const byId = new Map();
  for (const b of bookings || []) {
    for (const id of [b.bros_id, b.booking_id, b.id]) {
      if (id && !byId.has(String(id))) byId.set(String(id), b);
    }
  }
  const rows = [];
  for (const m of months || []) {
    for (const d of m.bookings || []) {
      const b = byId.get(String(d.booking_id)) || {
        bros_id: d.booking_id, enquiry_id: d.enquiry_id, currency: d.currency,
      };
      rows.push({
        ...b,
        status: d.cancellation ? 'cancellation' : b.status,
        _mk: m.month,
        _cur: 'GBP',
        _client: Number(d.turnover) || 0,
        _supplier: Number(d.cogs) || 0,
        _margin: Number(d.gross_margin) || 0,
        _share: d.cancellation ? null : Number(d.share),
        _nights: 0,
        _excluded: false,
      });
    }
  }
  return rows;
}

// Closing-date basis (Sales > Bookings): the whole booking in the month it
// was closed. closedOn maps booking id -> close date from the Sales tab
// (HubSpot close dates included); otherwise creation date, then check-in.
export function bookingByClosingMonth(b, fxRates, closedOn) {
  const closed = closedOn && (closedOn[b.bros_id] || closedOn[b.booking_id]);
  return {
    ...b,
    _mk: monthOf(closed || b.created_at || b.check_in),
    _cur: b.currency || 'GBP',
    _client: Number(b.client_total) || 0,
    _supplier: supplierCostInClientCurrency(b, fxRates),
    _margin: Number(b.margin) || 0,
    _share: null,
    _nights: b.num_nights || 0,
    _excluded: !!b.revenue_excluded,
  };
}

// Invoices by due date (issue date when there is none), ex VAT, the same
// basis as the P&L and Cashflow charts. Paid/outstanding are scaled to ex
// VAT in proportion, like the Cashflow Received bar. A client invoice that
// revenue does not count (revenueKeys from the dashboard drill) is listed
// but not totalled; while revenueKeys is unknown nothing is excluded.
export function invoiceByDueMonth(inv, paid, revenueKeys) {
  const gross = Number(inv.total_amount) || 0;
  const exVat = invoiceExVat(inv);
  const ratio = gross ? exVat / gross : 1;
  const isClient = (inv.type || 'client') === 'client';
  return {
    ...inv,
    _mk: monthOf(inv.due_date) || monthOf(inv.issue_date),
    _cur: inv.currency || 'GBP',
    _amount: exVat,
    _gross: gross,
    _paid: (Number(paid) || 0) * ratio,
    _outstanding: (Number(inv.amount_outstanding) || 0) * ratio,
    _excluded: Boolean(isClient && revenueKeys && !revenueKeys.has(invoiceKey(inv))),
  };
}
